import { FormArray, FormGroup } from "@angular/forms";
import { FormatDate, SelectMenuItem } from "@core/interfaces";
import { Platform, ValueTypeEnum, AffixEnum, RolModulePermissionEnum, RolModuleEnum } from "@core/enums";
import {  } from "@core/enums/platform";
import { DropdownEnum, TypeEnum, ValueTypeSimbolEnum } from "@core/enums";
import * as moment from "moment";
import { MenuItem } from "primeng/api";
import { ModuloForm } from "@core/models";

export function toCamelCase(str: string): string {
  return str
    .trim()
    .replace(/[-_\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''))
    .replace(/^[A-Z]/, (match) => match.toLowerCase());
}

export function toCapitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function toCapitalizeWithSpacing(str: string): string {
  return (
    str
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (firstChar) => firstChar.toUpperCase())
      .trim()
  );
}

export function toKebabCase(str: string): string {
  return str
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');
}


export function convertHour(time: string): string {
  return time.slice(0, 5);
}

export function transformDropdown<T, K extends keyof T, V extends keyof T>(
  data: T[],
  keyId: V,
  keyName: K,
  values?: unknown,
): MenuItem[] {
  if (data.length === 0) return [];

  return data.map((element) => {
    return {
      id: String(element[keyId]),
      name: String(element[keyName]),
      ...(values || {}),
    };
  });
}

type TransformedItem<T, K extends keyof T, C extends keyof T, V extends keyof T, Z extends (keyof T)[]> = {
  id: T[K];
  parentId?: T[C]|undefined;
  name: T[V];
} & {
  [P in Z[number]]: T[P];
};

export function transformSelectMenuItem<
  T extends object,
  K extends keyof T,
  V extends keyof T,
  C extends keyof T,
  Z extends (keyof T)[]
>(
  data: T[],
  keyId: K,
  keyName: V,
  keyParentId?: C|undefined,
  additionalProps?: Z|undefined
): TransformedItem<T, K, C, V, Z>[] {
  if (data.length === 0) return [];

  return data.map((element): TransformedItem<T, K, C, V, Z> => {
    const baseItem = {
      id: element[keyId],
      parentId: keyParentId ? element[keyParentId] : undefined,
      name: element[keyName],
    };

    if (!additionalProps || additionalProps.length === 0) {
      return baseItem as TransformedItem<T, K, C, V, Z>;
    }

    const extraProps = additionalProps.reduce((acc, prop) => {
      acc[prop] = element[prop];
      return acc;
    }, {} as { [P in Z[number]]: T[P] });

    return {
      ...baseItem,
      ...extraProps,
    };
  });
}


export function selectAllText(event: Event):void {
  const inputElement = event.target as HTMLInputElement;

  if (inputElement)
    setTimeout(() => {
      inputElement.setSelectionRange(0, inputElement.value.length);
    }, 0);
}

export function markFormGroupTouched(formGroup: FormGroup | FormArray, validate = false): void {
  Object.values((formGroup?.controls ?? [])).forEach(control => {
    if (control instanceof FormGroup) {
      // Si es un FormGroup, llamamos recursivamente a la función
      markFormGroupTouched(control, validate);
    } else if (control instanceof FormArray) {
      // Si es un FormArray, iteramos sobre sus controles
      markFormGroupTouched(control, validate);
    } else {
      // Si es un FormControl, lo marcamos como "touched" y "dirty"
      
      if (validate) {
        control.markAsTouched();
        control.markAsDirty();
        control.updateValueAndValidity();
      }
      
    }
  });
}

export function transformDate({ date, originalFormat = 'DD/MM/YYYY', format = 'YYYY-MM-DD' }: FormatDate): string|null {
  if (!date) return null;

  return moment(date, originalFormat).format(format);
}

export function convert12hTo24h({ date, originalFormat = 'hh:mm:ss A', format = 'HH:mm' }: FormatDate): string|null {
  if (!date) return null;
  
  // Extraer las horas de la cadena de tiempo
  const [hh] = date.split(':');
  const hours = parseInt(hh, 10);

  // Determinar si es AM o PM
  const period = (hours < 12 && hours > 0) ? 'AM' : 'PM';
  const dateFormat = `${date} ${period}`;

  // Devolver la cadena de tiempo con el periodo agregado
  return moment(dateFormat, originalFormat).format(format);
}

export function getValueTypeByValue(value: string | boolean, type = TypeEnum.Boolean): string|boolean {
  if (type === TypeEnum.String) {
    if (value) return ValueTypeEnum.Percentage;
    else return ValueTypeEnum.Amount;
  } else {
    if (value === ValueTypeEnum.Percentage) return true;
    else return false;
  }
}

export function getPlatforms(): SelectMenuItem<undefined>[] {
  return [
    { id: Platform.JuvoPos, name: toCapitalize(Platform.JuvoPos) },
    { id: Platform.JuvoOlo, name: toCapitalize(Platform.JuvoOlo) },
    { id: Platform.JuvoKiosk, name: toCapitalize(Platform.JuvoKiosk) },
  ];
}

export function getDropdown<T extends Record<string, string|undefined>>(enumType: T, nameKey: DropdownEnum = DropdownEnum.Value): MenuItem[] {
  return (Object.entries(enumType) ?? []).map(([key, value]) => ({
    id: value,
    name: toCapitalize(nameKey === DropdownEnum.Key ? key : value as string),
    value: value
  }));
}

export function transformPercentagePrefix(isPercentage: boolean, value: number): string {
  return value + (isPercentage ? AffixEnum.Percentage : AffixEnum.Amount);
}

export function transformToItemTable<T>(data: T, onlyNumbers = true): MenuItem[] { 
  return Object.entries(data as MenuItem)
  .filter(([label, value]) => (!onlyNumbers || typeof value == 'number') && label.slice(0,3) !== 'qty' )
  .map(([key, value]) => ({
    label: fromCamelCase(key),
    value: value as number,
    qty: (() : number | undefined => {
      for (const [name, val] of Object.entries(data as MenuItem).filter(([name, value]) => typeof value === 'number' && name.slice(0,3) === 'qty')) {
        if (toCapitalize(key).includes(name.slice(3))) return val;
      }
      return 0;
    })(),
  })) as MenuItem[];
}

export function fromCamelCase(str: string): string {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (match) => match.toUpperCase())
    .trim();
}

export function getEnumKeyByValue<T extends Record<string, string>>(enumObj: T, value: string): keyof T | undefined {
  return (Object.keys(enumObj) as Array<keyof T>).find(key => enumObj[key] === value);
}

export function getValueTypesSimbol(): MenuItem[] {
  return [
    { id: ValueTypeEnum.Amount, name: ValueTypeSimbolEnum.Amount },
    { id: ValueTypeEnum.Percentage, name: ValueTypeSimbolEnum.Percentage },
  ];
}
export function someValueTruthy<T extends object>(obj: { [key: string]: unknown }, ignoreKeys?: Array<keyof T>): boolean {
  if (!obj) return false;

  return Object.entries(obj)
    .filter(([ key ]) => !ignoreKeys || !(ignoreKeys ?? []).includes(key as keyof T))
    .some(([ _, value ]) => {
      if (value instanceof Array)
        return !!value.length

      return !!value
    }); 
}
  
export function getFormattedTime(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0'); // Obtiene las horas y asegura que tenga dos dígitos
    const minutes = String(date.getMinutes()).padStart(2, '0'); // Obtiene los minutos y asegura que tenga dos dígitos

    return `${hours}:${minutes}`; // Formatea la hora como HH:MM
}

export function getFormattedDate(date: Date): string {
    const year = date.getFullYear(); // Obtiene el año
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Obtiene el mes (Los meses son 0-indexados)
    const day = String(date.getDate()).padStart(2, '0'); // Obtiene el día

    return `${year}-${month}-${day}`; // Formatea la fecha como YYYY-MM-DD
}
export function validateTimeZone(timezone: string): boolean {
    try {
        new Intl.DateTimeFormat("en-US", { timeZone: timezone });
        return true;
    } catch {
        return false;
    }
}

export function getAllPermissions(modules: ModuloForm[]): (RolModuleEnum | RolModulePermissionEnum)[] {
  const permissions: (RolModuleEnum | RolModulePermissionEnum)[] = [];

  modules.forEach(module => {
    permissions.push(module?.code as RolModuleEnum);
    module.permissions.forEach(permission => {
      permissions.push(permission.code as RolModulePermissionEnum);
    });
  });

  return permissions;
}

export function getPermissions({ isSuperUser, isReseller, currentPermissions }: { isSuperUser: boolean, isReseller: boolean, currentPermissions: (RolModuleEnum | RolModulePermissionEnum)[] }): (RolModuleEnum | RolModulePermissionEnum)[] {
  return (!!isSuperUser
    ? [
        RolModuleEnum.Reseller,
        RolModulePermissionEnum.CreateReseller,
        RolModulePermissionEnum.EditReseller,
        RolModuleEnum.Account,
        RolModulePermissionEnum.CreateAccount,
        RolModulePermissionEnum.EditAccount,
        RolModulePermissionEnum.DeactivateAccount,
        RolModulePermissionEnum.ActivateAccount,
        RolModuleEnum.Store,
        RolModulePermissionEnum.CreateStore,
        RolModulePermissionEnum.EditStore,
        RolModulePermissionEnum.AddLicenses,
        RolModulePermissionEnum.EditLicenses,
        RolModulePermissionEnum.DeleteLicenses,
        RolModulePermissionEnum.DeactivateStore,
        RolModulePermissionEnum.ActivateStore,
        RolModuleEnum.User,
        RolModulePermissionEnum.ActivateUser,
        RolModulePermissionEnum.DeactivateUser,
        RolModuleEnum.Apk,
        RolModulePermissionEnum.CreateApk,
        RolModulePermissionEnum.EditApk,
        RolModulePermissionEnum.DeleteApk,
        RolModuleEnum.DelimiterSetting,
      ]
    : !!isReseller
      ?  [
          RolModuleEnum.Account,
          RolModulePermissionEnum.CreateAccount,
          RolModulePermissionEnum.EditAccount,
          RolModulePermissionEnum.DeactivateAccount,
          RolModulePermissionEnum.ActivateAccount,
          RolModuleEnum.Store,
          RolModulePermissionEnum.CreateStore,
          RolModulePermissionEnum.EditStore,
          RolModulePermissionEnum.AddLicenses,
          RolModulePermissionEnum.EditLicenses,
          RolModulePermissionEnum.DeleteLicenses,
          RolModulePermissionEnum.DeactivateStore,
          RolModulePermissionEnum.ActivateStore,
          RolModuleEnum.Role,
          RolModulePermissionEnum.CreateRole,
          RolModulePermissionEnum.EditRole, 
          RolModulePermissionEnum.DeleteRole,
          RolModuleEnum.User,
          RolModulePermissionEnum.CreateUser,
          RolModulePermissionEnum.EditUser,
          RolModulePermissionEnum.ActivateUser,
          RolModulePermissionEnum.DeactivateUser,
        ]
      : currentPermissions);
}

export function getRoles({ isSuperUser, isReseller, modules, currentModules }: { isSuperUser: boolean, isReseller: boolean, modules: ModuloForm[], currentModules: ModuloForm[] }): ModuloForm[] {

  if (!!currentModules && currentModules.length) return currentModules;

  const codesRole = (!!isSuperUser
    ? [
        RolModuleEnum.Reseller,
        RolModulePermissionEnum.CreateReseller,
        RolModulePermissionEnum.EditReseller,
        RolModuleEnum.Account,
        RolModulePermissionEnum.CreateAccount,
        RolModulePermissionEnum.EditAccount,
        RolModulePermissionEnum.DeactivateAccount,
        RolModulePermissionEnum.ActivateAccount,
        RolModuleEnum.Store,
        RolModulePermissionEnum.CreateStore,
        RolModulePermissionEnum.EditStore,
        RolModulePermissionEnum.AddLicenses,
        RolModulePermissionEnum.DeleteLicenses,
        RolModulePermissionEnum.DeactivateStore,
        RolModulePermissionEnum.ActivateStore,
        RolModuleEnum.Apk,
        RolModulePermissionEnum.CreateApk,
        RolModulePermissionEnum.EditApk,
        RolModulePermissionEnum.DeleteApk,
      ]
    : !!isReseller
      ?  [
          RolModuleEnum.Account,
          RolModulePermissionEnum.CreateAccount,
          RolModulePermissionEnum.EditAccount,
          RolModulePermissionEnum.DeactivateAccount,
          RolModulePermissionEnum.ActivateAccount,
          RolModuleEnum.User,
          RolModulePermissionEnum.CreateUser,
          RolModulePermissionEnum.EditUser,
          RolModulePermissionEnum.ActivateUser,
          RolModulePermissionEnum.DeactivateUser,
          RolModuleEnum.Role,
          RolModulePermissionEnum.CreateRole,
          RolModulePermissionEnum.EditRole, 
          RolModulePermissionEnum.DeleteRole,
          RolModuleEnum.Store,
          RolModulePermissionEnum.CreateStore,
          RolModulePermissionEnum.EditStore,
          RolModulePermissionEnum.AddLicenses,
          RolModulePermissionEnum.DeleteLicenses,
          RolModulePermissionEnum.DeactivateStore,
          RolModulePermissionEnum.ActivateStore,
        ]
      : []) as (RolModuleEnum | RolModulePermissionEnum)[];

  const codesSet = new Set<RolModuleEnum | RolModulePermissionEnum>(codesRole);

  return modules.map(({ id, code, name, permissions}) => {
    return {
      id,
      code,
      name,
      enabled: codesSet.has(code as RolModuleEnum),
      permissions: (permissions ?? []).map(({ id, code, name }) => ({
        id,
        code,
        name,
        enabled: codesSet.has(code as RolModulePermissionEnum),
      }))
    }
  })
}

export function hasRole<Z extends ModuloForm>(codeRole: string, modules: Z[]): boolean {
  return modules.some(({ code, enabled, permissions }) => (code === codeRole && !!enabled) || (permissions ?? []).some(({ code, enabled }) => (code === codeRole && !!enabled)));
}

export function hasPermission(codePermission: RolModuleEnum | RolModulePermissionEnum, permissions: (RolModuleEnum | RolModulePermissionEnum)[]): boolean {
  return permissions.includes(codePermission as RolModuleEnum | RolModulePermissionEnum);
}